import axios from 'axios'
import { get } from 'svelte/store'
import { server_path, font_upload_progress, local_fonts, detected_fonts } from '../stores'

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes - matching server limit

// Helper function to format file size for display
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Helper function to validate file size
export function validateFileSize(file) {
  return file.size <= MAX_FILE_SIZE;
}

// Sanitize filename to be safe for all operating systems
export function sanitizeFilename(filename) {
  // Replace invalid characters with underscore
  // Invalid chars: \\ / : * ? " < > |
  return filename.replace(/[\\\\/:*?"<>|]/g, '_')
    // Remove leading/trailing spaces and dots
    .replace(/^[\\s.]+|[\\s.]+$/g, '')
    // Replace multiple spaces/underscores with single underscore
    .replace(/[\\s_]+/g, '_')
    // Ensure filename isn't too long (max 255 chars, leaving room for extension)
    .slice(0, 240);
}

// Upload a single font file
async function uploadSingleFont(fontEntry) {
  console.log('Font Files - uploadSingleFont - uploading:', fontEntry.file.name);
  
  let form_data = new FormData();
  form_data.append('fonts[]', fontEntry.file);
  form_data.append('metadata[]', JSON.stringify(fontEntry.metadata));
  
  try {
    let response = await axios.post(get(server_path) + 'fonts/upload.php', form_data, { withCredentials: true });
    
    if (!response.data.success) {
      const errorMessage = response.data.errors ? response.data.errors.join('; ') : response.data.error || 'Unknown error';
      throw new Error(errorMessage);
    }
    
    return {
      success: true,
      font: response.data.fonts ? response.data.fonts[0] : null
    };
  } catch (error) {
    const errorMessage = error.response && error.response.data && error.response.data.error 
      ? error.response.data.error 
      : error.message;
    return {
      success: false,
      error: errorMessage
    };
  }
}

// New Central Orchestrator for all font uploads
export async function uploadAllFonts(fontEntries) {
  // fontEntries is an array of { file: File, metadata: { postscript_name: string, display_name: string } }
  console.log('Font Files - uploadAllFonts - starting upload for entries:', fontEntries);

  if (!fontEntries || fontEntries.length === 0) {
    console.log('Font Files - uploadAllFonts - no font entries provided.');
    return {
      data: {
        success: true, // No files to upload is a success in terms of operation
        upload_errors: [],
        fonts: []
      }
    };
  }

  let allErrors = [];
  let allUploadedFontsFromServer = []; // To store details from server response

  // Initialize tracking upload progress for individual font uploads
  font_upload_progress.set({
    total: fontEntries.length,
    uploaded: 0,
  });
  console.log('Font Files - uploadAllFonts - initializing upload progress', get(font_upload_progress));

  // Process fonts one by one
  for (let i = 0; i < fontEntries.length; i++) {
    const fontEntry = fontEntries[i];
    console.log(`Font Files - uploadAllFonts - uploading font ${i + 1} of ${fontEntries.length}: ${fontEntry.file.name}`);
    
    const result = await uploadSingleFont(fontEntry);
    
    if (result.success && result.font) {
      // Add to successful uploads
      allUploadedFontsFromServer.push(result.font);
      
      // Immediately update detected_fonts store for this uploaded font
      detected_fonts.update(fonts => 
        fonts.map(font => {
          if (font.postscript_name === result.font.postscript_name) {
            console.log(`Updating detected font ${font.postscript_name} as uploaded with file_name: ${result.font.filename}`);
            return { 
              ...font, 
              file_name: result.font.filename, 
              uploaded: true 
            };
          }
          return font;
        })
      );
      
      console.log(`Font Files - uploadAllFonts - successfully uploaded: ${fontEntry.file.name}`);
    } else {
      // Add error but continue with next font
      allErrors.push(`${fontEntry.file.name}: ${result.error}`);
      console.error(`Font Files - uploadAllFonts - failed to upload: ${fontEntry.file.name} - ${result.error}`);
    }
    
    // Update upload progress after each font
    font_upload_progress.update(progress => ({
      total: progress.total,
      uploaded: progress.uploaded + 1,
    }));
    console.log('Font Files - uploadAllFonts - updated progress', get(font_upload_progress));
  }
  
  const overallSuccess = allErrors.length === 0;
  
  if (overallSuccess) {
    console.log('Font Files - uploadAllFonts - all fonts processed successfully.');
  } else {
    console.log('Font Files - uploadAllFonts - some font uploads failed:', allErrors);
  }

  // Reset upload progress
  font_upload_progress.set(undefined);
  console.log('Font Files - uploadAllFonts - upload process finished, progress reset.');
  
  return {
    data: {
      success: overallSuccess,
      upload_errors: allErrors,
      fonts: allUploadedFontsFromServer
    }
  };
}

// Upload font files to the server
// This function is now a wrapper or can be deprecated if uploadAllFonts is used directly.
// For now, let's make it an internal helper or for specific cases if needed,
// but it should NOT manage font_upload_progress directly if used by uploadAllFonts.
// Given the new structure, this function might be largely redundant if callers prepare fontEntries correctly.
export async function uploadDroppedFontFiles(font_files, font_metadata_array) {
  console.log('Font Files - uploadDroppedFontFiles called. Consider using uploadAllFonts directly.');
  
  if (!font_files || font_files.length === 0) {
    return { data: { success: true, fonts: [], upload_errors: [] } };
  }

  const fontEntries = font_files.map((file, index) => {
    const metadata = font_metadata_array && font_metadata_array[index] 
      ? font_metadata_array[index]
      : { 
          postscript_name: sanitizeFilename(file.name.split('.').slice(0, -1).join('.')), // Best guess
          display_name: file.name.split('.').slice(0, -1).join('.') // Best guess
        };
    return { file, metadata };
  });

  return await uploadAllFonts(fontEntries);
}

// Upload font data from Font Access API
// This function is now a wrapper or can be deprecated.
// Callers should query local fonts and then use uploadAllFonts.
export async function uploadLocalFontsAsFontFiles(fontObjects) {
  console.log('Font Files - uploadLocalFontsAsFontFiles called. Consider querying fonts and using uploadAllFonts directly.');

  const localFontsStore = get(local_fonts);
  if (!localFontsStore) {
    console.error('Font Files - local fonts permission not available');
    return {
      data: {
        success: false,
        upload_errors: ['Local fonts permission not available'],
        fonts: []
      }
    };
  }

  let fontDataArray = [];
  try {
    const allLocalFonts = await self.queryLocalFonts();
    const neededPostscriptNames = new Set(fontObjects.map(f => f.postscript_name));
    fontDataArray = allLocalFonts.filter(font => neededPostscriptNames.has(font.postscriptName));
    
    if (fontDataArray.length === 0) {
      console.warn('Font Files - no matching local fonts found for upload.');
      return { data: { success: true, upload_errors: ['No matching local fonts found for upload.'], fonts: [] } };
    }
  } catch (error) {
    console.error('Font Files - error querying local fonts:', error);
    return {
      data: {
        success: false,
        upload_errors: [`Error querying local fonts: ${error.message}`],
        fonts: []
      }
    };
  }

  const fontEntries = await Promise.all(fontDataArray.map(async fontData => {
    const sanitizedName = sanitizeFilename(fontData.postscriptName);
    // Determine format. queryLocalFonts() might not give .format, so we infer or default.
    // Common font types: 'otf', 'ttf'. Some systems might give 'woff', 'woff2' from blobs.
    // The backend currently only accepts 'ttf', 'otf'.
    // For simplicity, we'll try to infer or default, but this might need refinement
    // based on what queryLocalFonts().blob() actually returns as type.
    const blob = await fontData.blob();
    let format = 'ttf'; // Default
    if (blob.type === 'font/otf') format = 'otf';
    else if (blob.type === 'font/ttf') format = 'ttf';
    // Add more specific type checks if needed, or rely on backend validation for now.

    const filename = `${sanitizedName}.${format}`;
    
    const metadata = {
      postscript_name: fontData.postscriptName,
      display_name: fontData.fullName || fontData.postscriptName,
      // uploaded_at will be added by uploadAllFonts or its batch processing
    };
    
    return {
      file: new File([blob], filename, { type: 'font/' + format }),
      metadata
    };
  }));

  if (fontEntries.length === 0) {
    console.warn('Font Files - failed to prepare any local font entries for upload.');
    // This case implies an issue with blob conversion or metadata prep,
    // though primary filtering is done above.
    return { data: { success: true, upload_errors: ['Failed to prepare local font entries.'], fonts: [] } };
  }
  
  return await uploadAllFonts(fontEntries);
}
